import React, { createContext, useContext, useState, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import api, { setAuthToken, setCompanyId } from '../utils/api';
import logger from '../utils/logger';

// Creo il contesto per l'autenticazione
const AuthContext = createContext();

// Hook personalizzato per usare il contesto di autenticazione
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere utilizzato all\'interno di un AuthProvider');
  }
  return context;
};

// Provider del contesto di autenticazione
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedCompanyId = localStorage.getItem('selectedCompanyId');
    // 🔒 SICUREZZA: Non loggare token
    logger.debug('Token presente:', !!token);
    logger.debug('🏢 Company ID salvato:', savedCompanyId);
    
    if (token) {
      try {
        // Imposta il token nell'header delle richieste
        setAuthToken(token);
        
        // Imposta l'ID dell'azienda se presente
        if (savedCompanyId) {
          setCompanyId(savedCompanyId);
          setSelectedCompany(parseInt(savedCompanyId));
        }
        
        logger.debug('📡 Chiamata /auth/me in corso...');
        // Ottieni i dati dell'utente corrente
        api.get('auth/me')
          .then(response => {
            logger.debug('✅ Risposta /auth/me ricevuta');
            setUser(response.data);
            // Correggi il controllo e salva companyId da response.data.companyId
            if (response.data.role !== 'supersaiyan' && response.data.companyId) {
              const receivedCompanyId = response.data.companyId;
              logger.debug("🏢 Impostazione azienda dell'utente da /me:", receivedCompanyId);
              setSelectedCompany(receivedCompanyId);
              setCompanyId(receivedCompanyId); // Imposta header/default
              localStorage.setItem('selectedCompanyId', receivedCompanyId.toString()); // Salva in localStorage
            }
          })
          .catch(err => {
            logger.error('❌ Errore nel caricamento dell\'utente:', err);
            // Logout in caso di errore
            logout();
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (err) {
        logger.error('❌ Errore nel decoding del token:', err);
        setUser(null);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      logger.debug('📡 Tentativo di login con:', email);
      const res = await api.post('auth/login', { email, password });
      // 🔒 SICUREZZA: Non loggare token nella risposta
      logger.debug('✅ Login completato con successo');
      
      const { token, user } = res.data;
      
      // Salva il token nel localStorage
      localStorage.setItem('token', token);
      
      // Salva freewheelMode nel localStorage per mostrare il logo al logout
      if (user.freewheelMode) {
        localStorage.setItem('isFreewheelUser', 'true');
      } else {
        localStorage.removeItem('isFreewheelUser');
      }
      
      // Imposta il token nell'header delle richieste
      setAuthToken(token);
      
      // Imposta i dati dell'utente
      setUser(user);
      
      // Se l'utente non è supersaiyan e ha un companyId, imposta l'azienda e salvala
      if (user.role !== 'supersaiyan' && user.companyId) {
        const receivedCompanyId = user.companyId;
        logger.debug("🏢 Impostazione azienda dell'utente da login:", receivedCompanyId);
        setSelectedCompany(receivedCompanyId);
        setCompanyId(receivedCompanyId); // Imposta header/default
        localStorage.setItem('selectedCompanyId', receivedCompanyId.toString()); // Salva in localStorage
      }
      
      return res.data;
    } catch (err) {
      logger.error('❌ Errore durante il login:', err);
      setError(err.response?.data?.msg || 'Errore durante il login');
      throw err;
    }
  };

  const logout = () => {
    logger.debug('🔒 Logout in corso...');
    // Rimuovi il token dal localStorage
    localStorage.removeItem('token');
    
    // Rimuovi il token dall'header delle richieste
    setAuthToken(null);
    
    // Rimuovi l'ID dell'azienda
    setCompanyId(null);
    
    // Reimposta l'utente e l'azienda selezionata
    setUser(null);
    setSelectedCompany(null);
  };

  const updateSelectedCompany = (companyId) => {
    logger.debug('🔄 Aggiornamento azienda selezionata:', companyId);
    setSelectedCompany(parseInt(companyId));
    setCompanyId(companyId);
    localStorage.setItem('selectedCompanyId', companyId);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error, 
      login, 
      logout,
      selectedCompany,
      updateSelectedCompany
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 